"use client";

import { getGradientAvatarUrl, useAppAccess, useAuth } from "@pex/auth/client";
import { AppContext, type AppContextType } from "@pex/types";
import {
  CreditCard,
  FileText,
  LogOut,
  Monitor,
  Moon,
  Sun,
  User,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { getAppConfigs } from "../../lib/app-config";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { AppSwitcher } from "./app-switcher";

export interface AuthNavControlsProps {
  /**
   * URL of the accounts app login page
   * @default process.env.NEXT_PUBLIC_ACCOUNTS_URL + '/login'
   */
  accountsLoginUrl?: string;
  /**
   * Avatar URL fetched server-side from shared.accounts. When provided this
   * takes precedence over user_metadata.avatar_url from the auth session,
   * ensuring migrated users see their profile picture without needing to
   * re-authenticate.
   */
  avatarUrl?: string | null;
  /**
   * URL of the billing portal. When provided the Billing item is shown in the
   * user menu; when omitted the item is hidden. The consuming layout is
   * responsible for determining eligibility (role check, subscription check)
   * and building the URL with any required context (app, org, etc.).
   */
  billingUrl?: string;
  /**
   * Custom className for the container
   */
  className?: string;
  /**
   * The key of the app this component is rendered in (e.g. "website", "bridge").
   * When provided, that app is excluded from the switcher so users aren't
   * shown a link back to the app they're already on.
   */
  currentApp?: string;
  /**
   * Hide the appearance / theme-cycle item in the user menu.
   * Use this on apps that don't support dark mode yet.
   */
  hideThemeToggle?: boolean;
  /**
   * Whether this is a protected page that requires authentication
   * - If true: signing out redirects to accounts app login
   * - If false: signing out just updates the UI to show Sign In button
   */
  requiresAuth?: boolean;
}

export function AuthNavControls({
  requiresAuth = false,
  accountsLoginUrl = `${process.env.NEXT_PUBLIC_ACCOUNTS_URL ?? ""}/login`,
  avatarUrl,
  className,
  billingUrl,
  currentApp,
  hideThemeToggle = false,
}: AuthNavControlsProps) {
  const { user, loading, signOut } = useAuth();
  const { userApps, loading: appsLoading } = useAppAccess();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => setMounted(true), []);

  const THEME_CYCLE = ["system", "light", "dark"] as const;
  type ThemeOption = (typeof THEME_CYCLE)[number];
  const THEME_META = {
    system: { label: "System", Icon: Monitor },
    light: { label: "Light", Icon: Sun },
    dark: { label: "Dark", Icon: Moon },
  } as const;
  const activeTheme = (theme ?? "system") as ThemeOption;
  const { label: themeLabel, Icon: ThemeIcon } = THEME_META[activeTheme];
  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(activeTheme);
    setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length] ?? "system");
  };

  // Always show Website + Finder when signed in (public apps with no AppContext).
  // Conditional apps (Bridge, Pulse, etc.) appear only once useAppAccess resolves;
  // they're hidden during loading so a deactivated facilitator never sees Bridge
  // flash, but the switcher itself stays mounted because the public apps are
  // always present — this is what fixes #235 (switcher missing on back-navigate).
  const accessibleApps = useMemo(() => {
    if (!user) {
      return [];
    }

    const appNameToContext: Record<string, AppContextType> = {
      Bridge: AppContext.BRIDGE,
      Pulse: AppContext.PULSE,
      Circle: AppContext.CIRCLE,
      Habitat: AppContext.HABITAT,
    };

    return getAppConfigs(currentApp).filter((app) => {
      const appContext = appNameToContext[app.name];
      // Apps with no AppContext (Website, Finder) are public — always show when logged in
      if (!appContext) {
        return true;
      }
      // Conditional apps require both: app-access resolved AND user has access
      return !appsLoading && userApps.includes(appContext);
    });
  }, [user, userApps, appsLoading, currentApp]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      if (requiresAuth) {
        window.location.href = accountsLoginUrl;
      }
    } catch (error) {
      // Surface the underlying error so users on dev can see it, and
      // un-stick the UI either way. The auth provider has already cleared
      // local state inside signOut(), so the user effectively is signed out
      // even when the upstream call to invalidate the refresh token fails.
      console.error("Sign out failed:", error);
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleSignIn = () => {
    window.location.href = accountsLoginUrl;
  };

  const handleManageAccount = () => {
    const accountsUrl = process.env.NEXT_PUBLIC_ACCOUNTS_URL ?? "";
    const returnTo = encodeURIComponent(window.location.href);
    window.location.href = `${accountsUrl}/manage?return_to=${returnTo}`;
  };

  const handleBilling = () => {
    if (billingUrl) {
      window.location.href = billingUrl;
    }
  };

  const handleMyDocuments = () => {
    const accountsUrl = process.env.NEXT_PUBLIC_ACCOUNTS_URL ?? "";
    window.location.href = `${accountsUrl}/documents`;
  };

  if (!mounted || loading) {
    return (
      <div className={`flex items-center gap-2 ${className || ""}`}>
        <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
      </div>
    );
  }

  if (isSigningOut) {
    return (
      <div className={`flex items-center gap-2 ${className || ""}`}>
        <Button disabled>Signing out...</Button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`flex items-center gap-2 ${className || ""}`}>
        <Button onClick={handleSignIn}>Sign In</Button>
      </div>
    );
  }

  const displayName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(" ");

  const avatarSrc =
    avatarUrl ?? user.avatar_url ?? getGradientAvatarUrl(user.email);

  return (
    <div className={`flex items-center gap-2 ${className || ""}`}>
      {/* App Switcher - only show if user has access to any apps */}
      {accessibleApps.length > 0 && <AppSwitcher apps={accessibleApps} />}

      {/* User Menu */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost">
            <Avatar>
              <AvatarImage alt={displayName || user.email} src={avatarSrc} />
              <AvatarFallback className="animate-pulse" />
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <div className="flex items-center gap-3 p-3">
            <Avatar size="lg">
              <AvatarImage alt={displayName || user.email} src={avatarSrc} />
              <AvatarFallback className="animate-pulse" />
            </Avatar>
            <div className="flex min-w-0 flex-col space-y-1">
              <p className="truncate font-medium text-sm leading-tight">
                {displayName}
              </p>
              <p className="truncate text-muted-foreground text-xs leading-tight">
                {user.email}
              </p>
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleManageAccount}
          >
            <User className="mr-2 h-4 w-4" />
            My account
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleMyDocuments}
          >
            <FileText className="mr-2 h-4 w-4" />
            My documents
          </DropdownMenuItem>
          {billingUrl && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleBilling}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              Billing
            </DropdownMenuItem>
          )}
          {!hideThemeToggle && (
            <>
              <DropdownMenuSeparator />
              {/* Appearance — cycles System → Light → Dark → System */}
              <DropdownMenuItem
                className="cursor-pointer gap-3"
                onClick={cycleTheme}
                onSelect={(e) => e.preventDefault()}
              >
                <ThemeIcon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-sm">{themeLabel}</span>
                <span className="rounded-md bg-muted px-2 py-0.5 text-muted-foreground text-xs">
                  Click to change
                </span>
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleSignOut}
            variant="destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
